import { arktypeResolver } from "@hookform/resolvers/arktype";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type } from "arktype";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";

import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { OverlayShell } from "@/components/ui/overlay";

import { FinanceiroCurrencyInput } from "../financeiro-currency-input";

const cashMovementForm = type({
	amount_cents: "number.integer > 0",
	occurred_on: "string",
	description: "string",
});

const CONTENT = {
	deposit: {
		title: "Registrar depósito",
		description: "O valor sai do caixa e o depósito bancário pode ser vinculado depois.",
		submit: "Registrar depósito",
		pending: "Registrando",
		success: "Depósito registrado",
	},
	withdrawal: {
		title: "Registrar retirada",
		description: "A retirada reduz o saldo esperado do caixa.",
		submit: "Registrar retirada",
		pending: "Registrando",
		success: "Retirada registrada",
	},
} as const;

export function CashMovementModal({
	kind,
	date,
	onClose,
}: {
	kind: "deposit" | "withdrawal";
	date: string;
	onClose: () => void;
}) {
	const queryClient = useQueryClient();
	const content = CONTENT[kind];
	const { mutate, isPending } = useMutation(
		orpc.financeiro.cash.create.mutationOptions({
			onSuccess: async () => {
				await queryClient.invalidateQueries({ queryKey: orpc.financeiro.key() });
				toast.success(content.success);
				onClose();
			},
			onError: () => toast.error("Não foi possível registrar o movimento"),
		}),
	);
	const {
		control,
		register,
		handleSubmit,
		trigger,
		formState: { isValid },
	} = useForm<typeof cashMovementForm.infer>({
		resolver: arktypeResolver(cashMovementForm),
		mode: "onChange",
		defaultValues: {
			amount_cents: 0,
			occurred_on: date,
			description: "",
		},
	});
	const close = () => {
		if (!isPending) {
			onClose();
		}
	};

	useEffect(() => {
		void trigger();
	}, [trigger]);

	return (
		<OverlayShell label={content.title} onClose={close} className="items-center justify-center p-4">
			<form
				aria-busy={isPending}
				onSubmit={handleSubmit((value) => mutate({ kind, ...value }))}
				className="overlay-panel max-w-md gap-4 p-5"
			>
				<div>
					<h2 className="type-section-title text-foreground">{content.title}</h2>
					<p className="mt-1 text-xs text-muted-foreground">{content.description}</p>
				</div>

				<div className="grid grid-cols-2 gap-3">
					<label className="flex flex-col gap-1.5 text-sm font-medium">
						Valor
						<Controller
							control={control}
							name="amount_cents"
							render={({ field }) => (
								<FinanceiroCurrencyInput
									valueCents={field.value}
									onValueCentsChange={field.onChange}
									disabled={isPending}
									slot="cash-movement-amount"
								/>
							)}
						/>
					</label>
					<label className="flex flex-col gap-1.5 text-sm font-medium">
						Data
						<Controller
							control={control}
							name="occurred_on"
							render={({ field }) => (
								<DatePicker
									value={field.value}
									onChange={(value) => value !== null && field.onChange(value)}
									disabled={isPending}
									containerClassName="w-full"
									className="w-full justify-between"
								/>
							)}
						/>
					</label>
				</div>

				<label className="flex flex-col gap-1.5 text-sm font-medium">
					Descrição
					<Input {...register("description")} disabled={isPending} placeholder="Opcional" />
				</label>

				<div className="flex justify-end gap-2">
					<Button type="button" variant="ghost" disabled={isPending} onClick={close}>
						Cancelar
					</Button>
					<Button type="submit" disabled={!isValid || isPending}>
						{isPending && <Loader2 className="animate-spin" />}
						{isPending && content.pending}
						{!isPending && content.submit}
					</Button>
				</div>
			</form>
		</OverlayShell>
	);
}
